import { useQuery, keepPreviousData } from '@tanstack/react-query';
import supabase from '@/api/supabase';
import { useEffect } from 'react';
import { startOfWeek, endOfWeek } from 'date-fns';

// Types for payroll data
interface TaskData {
  checkinTime: string;
  income: number;
  credits: number;
  supplies: number;
  empUserId: string;
  name: string;
}

interface DailySummary {
  day: string;
  date: string;
  fullDate: string;
  income: number;
  credits: number;
  supplies: number;
  tasks: TaskData[];
}

interface WeeklyData {
  records: DailySummary[];
  maxIncome: number;
  totalIncome: number;
}

// Function to get the ISO date range for a week (Monday to Sunday)
const getWeekDateRange = (date: Date) => {
  // Get start of week (Monday) and end of week (Sunday)
  const start = startOfWeek(date, { weekStartsOn: 1 }); // 1 = Monday
  const end = endOfWeek(date, { weekStartsOn: 1 });
  
  // Format dates as YYYY-MM-DD
  const startDate = start.toISOString().split('T')[0];
  const endDate = end.toISOString().split('T')[0];
  
  return {
    start: `${startDate}T00:00:00`,
    end: `${endDate}T23:59:59`,
  };
};

// Process weekly data into daily summaries
const processWeeklyData = (data: TaskData[]): WeeklyData => {
  const dailySummary: Record<string, DailySummary> = {};
  let maxIncome = 0;
  let totalIncome = 0;

  data.forEach(task => {
    const dateString = task.checkinTime.split('T')[0];
    
    if (!dailySummary[dateString]) {
      const tempDate = new Date(dateString + 'T00:00:00');
      const day = tempDate.toLocaleDateString('en-US', { 
        weekday: 'short',
        timeZone: 'UTC'  // Use UTC to avoid timezone shifts
      });
      const date = tempDate.getUTCDate().toString(); // Get date in UTC
      
      dailySummary[dateString] = {
        day,
        date,
        fullDate: dateString,
        income: 0,
        credits: 0,
        supplies: 0,
        tasks: [],
      };
    }

    dailySummary[dateString].tasks.push(task);
    dailySummary[dateString].income += task.income;
    dailySummary[dateString].credits += task.credits;
    dailySummary[dateString].supplies += task.supplies;
    totalIncome += task.income;

    maxIncome = Math.max(maxIncome, dailySummary[dateString].income);
  });

  const records = Object.values(dailySummary).sort((a, b) =>
    a.fullDate.localeCompare(b.fullDate)
  );

  return { records, maxIncome, totalIncome };
};

// Custom hook for fetching weekly payroll data
export const usePayrollWeek = (pickDate: Date) => {
  return useQuery({
    queryKey: ['payrollWeek', pickDate.toISOString()],
    queryFn: async () => {
      const dateRange = getWeekDateRange(pickDate);
      
      const { data: taskData, error: taskError } = await supabase
        .from('taskManager_history')
        .select('name, checkinTime, income, credits, supplies, empUserId')
        .gte('checkinTime', dateRange.start)
        .lt('checkinTime', dateRange.end)
        .order('checkinTime');

      if (taskError) {
        throw new Error('Failed to fetch payroll data');
      }

      return processWeeklyData(taskData || []);
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    placeholderData: keepPreviousData, // Keep header/picker mounted while week changes
  });
};

// Hook for real-time updates
export const usePayrollSubscription = (refetch: () => void) => {
  useEffect(() => {
    const channel = supabase
      .channel('payroll-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'taskManager_history' },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);
};

// Export the getWeekDateRange function for use in components
export { getWeekDateRange };
